import { Close } from "@mui/icons-material";
import {
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  TextField,
  Typography,
} from "@mui/material";

import HNSColors from "../../../../../../ui library/HNSColors";
import { useSmartyAutocomplete } from "../../../hooks/useSmartyAutoComplete";
import FieldLabel from "../../provider-details/FieldLabel";
import LoadingIndicator from "../LoadingIndicator";

const ClearButton = ({ onClick }) => (
  <InputAdornment position="end">
    <IconButton size="small" onClick={onClick}>
      <Close fontSize="small" />
    </IconButton>
  </InputAdornment>
);

const getHelperText = ({ hasKey, error, value }) => {
  if (!hasKey) {
    return "Unauthorized, please contact admin";
  }
  if (error) {
    return "Unable to fetch address suggestions";
  }
  if (value && value.length < 5) {
    return "Search must be at least 5 characters";
  }
  return "";
};

export default function SmartyAutoComplete({
  address,
  setAddress,
  hideLabel,
  inputSx,
  inputRequired = false,
  primaryFieldLabel = "Address",
}) {
  const {
    searchVal,
    suggestions,
    smartyError,
    loading,
    isFocused,
    handleSearch,
    handleClear,
    handleSelect,
    handleBlur,
    handleFocus,
    formatDisplayedAddress,
    manualAddressChange,
    clearAddressField,
  } = useSmartyAutocomplete({ address, setAddress });

  const hasKey = Boolean(process.env.REACT_APP_SMARTY_API_KEY);
  const showSuggestions = isFocused && searchVal?.length >= 5;

  return (
    <div className="relative flex gap-2">
      <div className="w-2/5">
        <FieldLabel value={primaryFieldLabel} />

        <TextField
          className="w-full"
          sx={inputSx}
          required={inputRequired}
          label={hideLabel ? "" : "Address"}
          value={address?.raw ?? searchVal}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={!hasKey}
          error={smartyError}
          helperText={getHelperText({
            hasKey,
            error: smartyError,
            value: searchVal,
          })}
          InputProps={{
            endAdornment: loading ? (
              <InputAdornment position="end">
                <LoadingIndicator defaultPadding="py-1" />
              </InputAdornment>
            ) : (
              address && <ClearButton onClick={handleClear} />
            ),
          }}
        />

        {showSuggestions && (
          <List className="absolute z-10 w-full bg-white shadow-md">
            {suggestions.length ? (
              suggestions.map((s, i) => (
                <ListItemButton key={i} onClick={() => handleSelect(s)}>
                  {formatDisplayedAddress(s)}
                </ListItemButton>
              ))
            ) : (
              <ListItem disablePadding>
                <Typography
                  className="px-2 py-2 text-[14px]"
                  sx={{ color: HNSColors.neutrals[400] }}
                >
                  Nothing to show
                </Typography>
              </ListItem>
            )}
          </List>
        )}
      </div>

      <div className="flex flex-1 gap-2">
        {[
          {
            label: "Suite Type",
            key: "suiteType",
            placeholder: "Apartment, Suite, Unit, etc.",
          },
          {
            label: "Suite Number",
            key: "suiteNumber",
            placeholder: "Suite Number - ex: 123",
          },
        ].map(({ label, key, placeholder }) => (
          <div key={key} className="w-full">
            <FieldLabel value={label} />
            <TextField
              className="w-full"
              sx={inputSx}
              placeholder={placeholder}
              value={address?.[key] ?? ""}
              onChange={(e) => manualAddressChange(e.target.value, key)}
              InputProps={{
                endAdornment: address?.[key] && (
                  <ClearButton onClick={() => clearAddressField(key)} />
                ),
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
